/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, {
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Backdrop,
  CircularProgress,
  Snackbar,
  Breadcrumbs,
  Link,
  Container
} from "@mui/material";
import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchApplicationDetails } from "../../../Services/redux/appId-details/AppIdDeatilsActions";
import "./PremiumProducts.css";
import Topheader from "../../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationDetailsErrorDialog from "../../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import APIManager from "../../../Services/ApiManager/ApiManager";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import RepeatIcon from "@mui/icons-material/Repeat";
import EditIcon from "@mui/icons-material/Edit";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Constants
const SUBMISSION_DETAILS_EMPTY_CHECK = (details) =>
  details && Object.keys(details).length > 0;
const IS_DRAFT = (details) => SUBMISSION_DETAILS_EMPTY_CHECK(details) && details.status === "DRAFT";
const IS_NOT_DRAFT = (details) => SUBMISSION_DETAILS_EMPTY_CHECK(details) && details.status !== "DRAFT";

// Navigation state builder
const buildNavigationState = (teamId, sellerId, product, allPlans, oneTimeplanExists, usedRecurringFrequencies) => ({
  state: {
    teamId,
    sellerId,
    product,
    allPlans,
    oneTimeplanExists,
    usedRecurringFrequencies,
  },
});

// Style getters
const getContainerStyle = (submissionDetails) =>
  IS_DRAFT(submissionDetails)
    ? {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }
    : { display: "flex", flexDirection: "row", width: "1260px" };

const getSideMenuStyle = (submissionDetails) =>
  IS_NOT_DRAFT(submissionDetails)
    ? { marginTop: "69px" }
    : { marginTop: "0px" };

const getSubmissionContainerStyle = (submissionDetails) =>
  IS_NOT_DRAFT(submissionDetails)
    ? { width: "93%", marginTop: "42px", marginLeft: "70px" }
    : { marginLeft: "0", marginTop: "30px" };

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function createData(type, name, price, currencyCode, planId, lastUpdated) {
  return {
    type,
    name,
    price,
    currencyCode,
    planId,
    lastUpdated,
    lastModified: lastUpdated,
  };
}

function buildOneTimePlan(oneTimePlan) {
  return {
    name: oneTimePlan.name,
    price: oneTimePlan.basePrice.price,
    currencyCode: oneTimePlan.basePrice.currencyCode,
    planId: oneTimePlan.id,
    type: oneTimePlan.planType,
    lastUpdated: oneTimePlan.lastUpdated,
  };
}

function buildRecurringPlansData(recurringPlans) {
  const plans = [];
  const frequencies = [];
  recurringPlans.forEach((element) => {
    frequencies.push(`${element.recurringFrequency}-${element.recurringFrequencyUnit}`);
    plans.push({
      name: element.name,
      price: element.basePrice.price,
      currencyCode: element.basePrice.currencyCode,
      planId: element.id,
      type: element.planType,
      lastUpdated: element.lastUpdated,
    });
  });
  return { plans, frequencies };
}

const headCells = [
  { id: "planName", numeric: false, disablePadding: false, label: "Plan Name" },
  { id: "price", numeric: false, disablePadding: false, label: "Base Price" },
  { id: "offers", numeric: false, disablePadding: false, label: "Offers" },
  { id: "lastModified", numeric: true, disablePadding: false, label: "Last Modified" },
];

const MONTH_NAMES = [
  "Jan", "Feb", "March", "Apr", "May", "June",
  "July", "August", "Sept", "Oct", "Nov", "Dec",
];

function Row({ row, onEdit }) {
  const date = new Date(row.lastUpdated);
  const formattedDate = `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  return (
    <TableRow hover tabIndex={-1} key={row.planId} sx={{ cursor: "pointer", height: "32px" }}>
      <TableCell component="th" scope="row" align="left" sx={{ fontWeight: "bold", padding: "8px 16px" }}>
        {row.name}{" "}
        {row.type === "RECURRING" && <RepeatIcon sx={{ fontSize: 20 }} />}
      </TableCell>
      <TableCell align="left" sx={{ padding: "8px 16px" }}>
        {row.currencyCode} {row.price.toFixed(2)}
      </TableCell>
      <TableCell align="left" sx={{ padding: "8px 16px" }}>0</TableCell>
      <TableCell align="left" sx={{ padding: "8px 16px" }}>
        <Grid container spacing={2}>
          <Grid size={10}>{formattedDate}</Grid>
          <Grid size={2}>
            <span className="icons-cont">
              <IconButton aria-label="expand row" size="small" onClick={() => onEdit(row.planId)}>
                <EditIcon />
              </IconButton>
            </span>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
}

Row.propTypes = {
  row: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
};

function EnhancedTableHead({ order, orderBy, rowCount, onRequestSort }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead className="tableHeaderCls">
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ fontWeight: "bold", padding: "8px 16px" }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function PremiumProducts(props) {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.state.teamId;
  const [packageName, setPackageName] = useState("");
  const [sellerId, setSellerId] = useState("");
  const { loading, submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("lastModified");
  const [rows, setRows] = React.useState([]);
  const page = 0;
  const rowsPerPage = 5;
  const [paidApp, setPaidApp] = React.useState(false);
  const [oneTimeplanExists, setOneTimeplanExists] = React.useState(false);

  const hasPremiumPaymentScope = () => {
    return sessionStorage
      .getItem("payment_role")
      .includes("IGNITESTOREMONETIZEPREMIUM_SCP");
  };


  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows]
  );

  const errors = useSelector((state) => state.errors);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(true);
  const [plansList, setPlansList] = useState([]);
  const [product, setProduct] = useState([]);
  const [showAddPlanButton, setShowAddPlanButton] = useState(true);
  const [usedRecurringFrequencies, setUsedRecurringFrequencies] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");

  useEffect(() => {
    if (appData?.submissions && appData.submissions.length > 0) {
      dispatch(fetchSubmissionDetails(appId, appData.submissions[0].id));
    }
  }, [appData]);
  useEffect(() => {
    dispatch(fetchApplicationDetails(appId));
  }, [appId]);
  useEffect(() => {
    if (submissionDetails && Object.keys(submissionDetails).length > 0) {
      if (submissionDetails?.binary?.packageName && !packageName) {
        setPackageName(submissionDetails.binary.packageName);
        getUserDetails(submissionDetails.binary.packageName);
      } else {
        // For DRAFT status or when binary is not available, stop loading
        setLoader(false);
      }
    }
  }, [submissionDetails, packageName]);


  const goToPlan = () => {
    navigate(`/ui/developer/applications/${appId}/premium-product/plan`, 
      buildNavigationState(teamId, sellerId, product, plansList, oneTimeplanExists, usedRecurringFrequencies)
    );
  };

  const getUserDetails = (packageName) => {
    APIManager.getUserInfo().then((json) => {
      if (
        json.messages?.[0]?.key &&
        json.messages[0]?.key === "success.key"
      ) {
        getAllPremiumApps(json?.results[0].id, packageName);
        setSellerId(json?.results[0].id);
      } else {
        setLoader(false);
      }
    });
  };

  const getAllPremiumApps = (sellerId, packageName) => {
    APIManager.getAllPremiumApps(sellerId, packageName).then((json) => {
      if (json[0]?.product) {
        const allPlans = [];
        setProduct(json[0]);
        if (json[0].product.id !== null) setPaidApp(true);

        if (json[0].oneTimePlan === null) {
          setOneTimeplanExists(false);
        } else {
          setOneTimeplanExists(true);
          allPlans.push(buildOneTimePlan(json[0].oneTimePlan));
        }

        if (
          (hasPremiumPaymentScope && oneTimeplanExists && json[0].recurringPlans.length === 4) ||
          (!hasPremiumPaymentScope && oneTimeplanExists)
        ) {
          setShowAddPlanButton(false);
        }

        if (json[0].recurringPlans.length > 0) {
          const { plans, frequencies } = buildRecurringPlansData(json[0].recurringPlans);
          plans.forEach((p) => allPlans.push(p));
          setUsedRecurringFrequencies(frequencies);
        }

        setPlansList(allPlans);
        if (allPlans.length > 0) formTableWithData(allPlans);
      } else {
        setPlansList([]);
        setRows([]);
        setProduct([]);
        setOneTimeplanExists(false);
        setUsedRecurringFrequencies([]);
      }
      setLoader(false);
    });
  };

  const formTableWithData = (plans) => {
    let allRows = [];
    plans.forEach((plan) => {
      allRows.push(
        createData(
          plan.type,
          plan.name,
          plan.price,
          plan.currencyCode,
          plan.planId,
          plan.lastUpdated
        )
      );
    });
    setRows(allRows);
  };

  const backToApplications = () => {
    navigate(-1);
  };

  const goToEditPage = (planId) => {
    navigate(
      `/ui/developer/applications/${appId}/premium-product/${product.product.id}/plan/${planId}`,
      buildNavigationState(teamId, sellerId, product, plansList, oneTimeplanExists, usedRecurringFrequencies)
    );
  };
  const handleChange = (event) => {
    if (event.target.checked) {
      setPaidApp(event.target.checked);
    } else if (plansList.length > 0) {
      setOpenDeleteDialog(true);
    }
    setPaidApp(event.target.checked);
  };
  const deletePremiumApps = () => {
    setLoader(true);
    setOpenDeleteDialog(false);
    APIManager.deleteAppPricing(packageName).then((json) => {
      setPaidApp(false);
      getAllPremiumApps(sellerId, packageName);
      setLoader(false);
      setOpenSnackbar(true);
      setSnackMsg(`Deleted successfully`);
      setTimeout(function () {
        navigate(
          `/ui/developer/applications/${appId}/premium-product?deleted=true`,
          { state: { teamId: teamId } }
        );
      }, 3000);
    });
  };
  const handleCancel = () => {
    setOpenDeleteDialog(false);
    setPaidApp(true);
  };

  return (
    <>
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Topheader />
      {
        <Container maxWidth="lg">
                          <Grid container>
                            <Grid items xs={12} sm={12} md={12} lg={12}></Grid>
        <div
          className="container"
          style={getContainerStyle(submissionDetails)}
        >
          <div
            className="side-menu"
            style={getSideMenuStyle(submissionDetails)}
          >
            <button
              className="arrow-back"
              onClick={backToApplications}
              onKeyDown={(e) => e.key === "Enter" && backToApplications()}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
            >
              <div>
                <ArrowBackIcon style={{ color: " #717273" }} />
              </div>
              <div className="my-apps">App Overview</div>
            </button>
            {IS_NOT_DRAFT(submissionDetails) &&
              appData &&
              Object.keys(appData).length > 0 && (
                <ApplicationDetailsSideMenu
                  appData={appData}
                  isMonetized={submissionDetails.isMonetized}
                />
              )}
          </div>
          <div
            className="submission-container"
            style={getSubmissionContainerStyle(submissionDetails)}
          >
            <section className="listing-header-container">
              <div style={{ marginLeft: "16%", marginBottom: "15px" }}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    onClick={backToApplications}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackIcon
                      sx={{ mr: 0.5, marginRight: "19px", color: "#b3b3b3" }}
                      fontSize="inherit"
                    />
                    {SUBMISSION_DETAILS_EMPTY_CHECK(submissionDetails) && (
                        <>{submissionDetails.descriptions[0].applicationName}</>
                      )}
                  </Link>
                  <Link
                    underline="none"
                    sx={{ fontWeight: "500", color: "#2a2c2d" }}
                  >
                    App Pricing
                  </Link>
                </Breadcrumbs>
              </div>
              {SUBMISSION_DETAILS_EMPTY_CHECK(submissionDetails) && (
                <div className="container-header-icons">
                  <div className="app-icon" style={{ marginLeft: "16%" }}>
                    <div className="icon-name">
                      <img
                        src={submissionDetails.icon.url}
                        alt={submissionDetails.fileName}
                        width={80}
                        height={80}
                      />
                      {
                        <div className="name-status">
                          <p className="application-name">
                            {
                              submissionDetails.descriptions[0]
                                .applicationName
                            }
                          </p>
                        </div>
                      }
                    </div>
                  </div>
                  {packageName !== "" && (
                    <div
                      className="submit-buttons"
                      style={{ marginRight: "5%" }}
                    >
                      <FormControlLabel
                        value="end"
                        control={
                          <Switch
                            size="medium"
                            color="primary"
                            checked={paidApp}
                            onChange={handleChange}
                          />
                        }
                        label="Make your app paid"
                        labelPlacement="start"
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
            <div
              className="in-app-title"
              style={{ marginTop: "1%", fontWeight: "bolder" }}
            >
              Premium App
            </div>
            {packageName === "" && (
              <div
                className="products-container"
                style={{ marginRight: "6%", marginLeft: "16%", marginTop: "10%" }}
              >
                <div className="col-12 blankCls app-list-blank">
                  <div className="textErrorMsg">
                    Please upload a binary to enable premium app features.
                  </div>
                </div>
              </div>
            )}
            {paidApp && (
              <div
                className="products-container"
                style={{ marginRight: "6%", marginLeft: "16%" }}
              >
                {packageName === "" && (
                  <div className="col-12 blankCls app-list-blank">
                    <div className="textErrorMsg">
                      Sorry, you cannot create product for this application!
                    </div>
                  </div>
                )}
                {rows.length > 0 && (
                  <Box sx={{ width: "100%" }}>
                    <Paper sx={{ width: "100%", mb: 2 }}>
                      <TableContainer component={Paper}>
                        <Table aria-label="collapsible table">
                          <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                          />
                          <TableBody>
                            {visibleRows.map((row) => (
                              <Row key={row.planId} row={row} onEdit={goToEditPage} />
                            ))}
                            {emptyRows > 0 && (
                              <TableRow
                                style={{
                                  height: 32 * emptyRows,
                                }}
                              >
                                <TableCell colSpan={6} />
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Box>
                )}
                {showAddPlanButton && (
                  <Button
                    sx={{ textTransform: "none" }}
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={goToPlan}
                  >
                    Add Plan
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        </Grid>
                        </Container>
      }
      {errors?.errorMessage != null && errors.errorType != null && (
        <ApplicationDetailsErrorDialog />
      )}
      {(appData.loading || loading) && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
      <Dialog
        sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 435 } }}
        maxWidth="xs"
        open={openDeleteDialog}
      >
        <DialogTitle sx={{ color: "red" }}>Make Premium App Free</DialogTitle>
        <DialogContent dividers>
          <p className="delist-text">
            You are about to make premium app free.All the edits made will be
            lost. Are you sure?
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            className={`custom-button submitted_button`}
            style={{
              width: "fit-content",
              height: "fit-content",
              maxWidth: "100%",
              textTransform: "none",
            }}
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            className={`custom-button delete_button`}
            style={{
              width: "fit-content",
              height: "fit-content",
              maxWidth: "100%",
              textTransform: "none",
            }}
            sx={{
              "&.Mui-disabled": {
                // color: 'rgba(0, 0, 0, 0.26) !important',
                // border: '1px solid rgba(0, 0, 0, 0.12) !important'
                border: "1px solid rgba(181, 11, 0, 1) !important",
                color: "rgba(181, 11, 0, 1) !important",
                opacity: 0.5,
              },
            }}
            variant="outlined"
            onClick={deletePremiumApps}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PremiumProducts;